import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import cn from 'classnames';
import { User } from 'common/interfaces/api';
import { ableToShowSubscribingStatusModal } from 'common/utils/payment';
import ConfirmDialog from 'components/common/ConfirmDialog';
import ReportBox from 'components/common/ReportBox';
import SubscribingStatusModal from 'components/Profile/SubscribingStatusModal';
import {
  BlockUserIcon,
  ReportIcon,
  SubscriptionIcon,
  ThreeDotsIcon,
} from 'components/v2/atoms/icon';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';

interface MoreOptionsPropsType {
  user: User;
}

const MoreOptions = ({ user }: MoreOptionsPropsType) => {
  const currentUser = useStore((state) => state.currentUser);
  const showLogin = useGlobalStore((state) => state.showLogin);
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage,
  );

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showSubscribingStatusModal, setShowSubscribingStatusModal] =
    useState(false);
  const [openConfirmBlockUser, setOpenConfirmBlockUser] = useState(false);
  const [openConfirmReportUser, setOpenConfirmReportUser] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleDropdown = () => {
    if (!currentUser) {
      showLogin({});
      return;
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleBlockUser = async () => {
    try {
      await axios.post(`/users/blocks`, {
        user_id: user.id,
        action: 'block',
      });
      setSnackbarMessage({
        text: 'ユーザーがブロックされました',
        type: 'success',
      });
      setOpenConfirmBlockUser(false);
    } catch (e) {
      setSnackbarMessage({
        type: 'error',
        text: 'ユーザーのブロックが失敗しました',
      });
    }
    setIsDropdownOpen(false);
  };

  const handleOpenBlockDialog = () => {
    setIsDropdownOpen(false);
    setOpenConfirmBlockUser(true);
  };

  const handleOpenReportDialog = () => {
    setIsDropdownOpen(false);
    setOpenConfirmReportUser(true);
  };

  const handleOpenSubscriptionModal = () => {
    setIsDropdownOpen(false);
    setShowSubscribingStatusModal(true);
  };

  return (
    <>
      <div ref={dropdownRef} className="relative">
        {/* Trigger Button */}
        <button
          onClick={handleToggleDropdown}
          className={cn(
            'flex size-9 items-center justify-center rounded-full border border-solid border-pp-border-primary transition-colors',
            'bg-white text-pp-text-primary hover:bg-pp-bg-secondary',
          )}
        >
          <ThreeDotsIcon className="size-5" />
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <div className="absolute right-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-lg border border-pp-border-primary bg-white shadow-lg">
            {/* Block User Option */}
            <button
              onClick={handleOpenBlockDialog}
              className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-pp-bg-secondary"
            >
              <BlockUserIcon className="size-5 shrink-0 text-pp-text-primary" />
              <span className="text-pp-text-primary">
                ユーザーをブロックする
              </span>
            </button>

            {/* Report User Option */}
            <button
              onClick={handleOpenReportDialog}
              className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-pp-bg-secondary"
            >
              <ReportIcon className="size-5 shrink-0 text-pp-text-primary" />
              <span className="text-pp-text-primary">
                ユーザーをレポートする
              </span>
            </button>

            {/* Manage Prime Subscription Option */}
            {ableToShowSubscribingStatusModal(user) && (
              <>
                <div className="border-t border-pp-border-primary" />
                <button
                  onClick={handleOpenSubscriptionModal}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors hover:bg-pp-bg-secondary"
                >
                  <SubscriptionIcon className="size-5 shrink-0 text-pp-text-primary" />
                  <span className="text-pp-text-primary">
                    プライム登録を管理する
                  </span>
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Block User Confirm Dialog */}
      {openConfirmBlockUser && (
        <ConfirmDialog
          open={openConfirmBlockUser}
          actionCloseFnc={() => setOpenConfirmBlockUser(false)}
          actionSelectedFnc={handleBlockUser}
          title="本当にブロックしますか？"
          yesText="ブロックする"
        />
      )}

      {/* Report User Dialog */}
      {openConfirmReportUser && (
        <ConfirmDialog
          open={openConfirmReportUser}
          actionCloseFnc={() => setOpenConfirmReportUser(false)}
          title="ユーザーをレポートする"
          withTextBox
          textBox={
            <ReportBox
              targetUserId={user.id}
              onDialogSubmit={() => setOpenConfirmReportUser(false)}
            />
          }
        />
      )}

      {/* Subscribing Status Modal */}
      {currentUser && user && showSubscribingStatusModal && (
        <SubscribingStatusModal
          creator={user}
          open={showSubscribingStatusModal}
          setOpen={setShowSubscribingStatusModal}
        />
      )}
    </>
  );
};

export default MoreOptions;
